import { useMemo } from "react";
import { useContent } from "./content";
import { useLang } from "./lang";

/**
 * useLangKV — returns a KV object with translated fields overlaid on top of the
 * PT source. Convention:
 *   kv[key] = { field1: "...", field2: "...", translations: { en: {...}, es: {...} } }
 *
 * When active `lang !== "pt"`, any field present in `translations[lang]` overrides
 * the PT source. Missing translations fall back to the PT source automatically.
 *
 * Example usage in a page:
 *   const sobre = useLangKV("sobre");           // returns object with active-lang overlay
 *   <h1>{sobre.pillars_title}</h1>              // in EN → translations.en.pillars_title
 *
 * The overlay only applies to string fields and known nested containers; complex
 * objects (arrays of records, e.g. `pillars[]`) are passed through unchanged.
 */
export function useLangKV(kvKey) {
  const content = useContent();
  const { lang } = useLang();
  const raw = content[kvKey];
  return useMemo(() => overlay(raw, lang), [raw, lang]);
}

export function overlay(raw, lang) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  if (!lang || lang === "pt") return raw;
  const t = raw.translations && raw.translations[lang];
  if (!t || typeof t !== "object") return raw;
  const out = { ...raw };
  Object.keys(t).forEach((k) => {
    const v = t[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
}
